/* istanbul ignore file */
/* istanbul ignore file */
import * as codec from 'purify-ts/Codec';
import { array as codecArray, string as codecString } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, paginated, requestParams } from '@codegrade/api-core';
import { PlagiarismCase, PlagiarismMatch, PlagiarismRun } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function plagiarism(config) {
    const deleteResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const getResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(PlagiarismRun)),
    });
    const getProvidersResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ codecArray(
        /* #__PURE__ */ codec.record(codecString, codec.unknown)))),
    });
    const getCaseResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(PlagiarismCase)),
    });
    const getLogResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parsers.parseArrayBufferResponse),
    });
    const getMatchesResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(PlagiarismMatch))),
    });
    const getCasesResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(PlagiarismCase))),
    });
    return Object.freeze({
        /**
         * Delete a given plagiarism run and all its cases.
         *
         * This is irreversible, so make sure the user really wants this!
         *
         * @param arg The arguments to the function.
         * @param arg.plagiarismId The id of the run to delete.
         * @returns Nothing.
         */
        delete: async ({ plagiarismId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/plagiarism/${plagiarismId}`, deleteResponse.v, undefined);
            return result;
        },
        /**
         * Get a `.models.PlagiarismRun`.
         *
         * @param arg The arguments to the function.
         * @param arg.plagiarismId The of the plagiarism run.
         * @returns An single plagiarism run.
         */
        get: async ({ plagiarismId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/plagiarism/${plagiarismId}`, getResponse.v, undefined);
            return result;
        },
        /**
         * Get all plagiarism providers for this instance.
         *
         * @param arg The arguments to the function.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns An array of plagiarism providers.
         */
        getProviders: async ({ pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/plagiarism/`, getProvidersResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get a single plagiarism case.
         *
         * @param arg The arguments to the function.
         * @param arg.plagiarismId The id of the run the case should belong to.
         * @param arg.caseId The id of the case requested.
         * @returns The requested case.
         */
        getCase: async ({ plagiarismId, caseId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/plagiarism/${plagiarismId}/cases/${caseId}`, getCaseResponse.v, undefined);
            return result;
        },
        /**
         * Get the log of a plagiarism run.
         *
         * @param arg The arguments to the function.
         * @param arg.plagiarismId The id of the run of which you want to get the log.
         * @returns The log of the run.
         */
        getLog: async ({ plagiarismId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/plagiarism/${plagiarismId}/log`, getLogResponse.v, undefined);
            return result;
        },
        /**
         * Get the matches in a plagiarism case.
         *
         * @param arg The arguments to the function.
         * @param arg.plagiarismId The id of the run the case should belong to.
         * @param arg.caseId The id of the case requested.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns The matches of this case, ordered by id.
         */
        getMatches: async ({ plagiarismId, caseId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/plagiarism/${plagiarismId}/cases/${caseId}/matches/`, getMatchesResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get all the `.models.PlagiarismCase`s for the given `.models.PlagiarismRun`.
         *
         * @param arg The arguments to the function.
         * @param arg.plagiarismId The of the plagiarism run.
         * @param arg.q Search the cases based on this value.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns An array of plagiarism cases.
         */
        getCases: async ({ plagiarismId, q = undefined, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/plagiarism/${plagiarismId}/cases/`, getCasesResponse.v, undefined, {
                q,
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
    });
}
